import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Empty, Spin, Tag, Tooltip, Typography, message } from 'antd';
import { DownloadOutlined, EyeOutlined, CodepenOutlined, FileOutlined, UploadOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { ingestionApi } from '../ingestionApi';
import { formatBytes } from '../ingestionHelpers';
import { catalogImageUrl } from '../../images';
import { ModelViewer } from './ModelViewer';
import { isContainerMeshName } from './ModelEditor';

const { Text } = Typography;

const isModel = (fmt) => fmt === 'glb' || fmt === 'babylon';

// Labels the pipeline already counts as the variant's diffuse texture — these
// rows show an "asset" tag instead of the "Use as asset" action.
const DIFFUSE_LABELS = ['texture-gen', 'wallprint', 'manual-upload', 'manual-select'];

/** Builds the unified file list: the current cutout, bare-frame edits, asset files, then scraped images. */
const buildFiles = (assets, externalImages, cutoutImageId, modelImageUrls, bedFrameImageIds) => {
    // Images sent to meshy: the explicit multi-image selection, else the cutout —
    // except for beds, where the bedding-free edits of those images go instead.
    const selectedSources = new Set((modelImageUrls || []).filter(Boolean));
    const frameIds = bedFrameImageIds || [];
    const isSource = (url) => frameIds.length === 0 && (selectedSources.size === 0 ? url === cutoutUrl : selectedSources.has(url));
    // The chosen/uploaded variant image lives on the variant (imageId), not as an
    // asset row, so surface it explicitly — otherwise an uploaded cutout never shows.
    // It's the model source either by default (no explicit selection) or when it
    // was explicitly picked into the multi-image set.
    const cutoutUrl = cutoutImageId ? catalogImageUrl(cutoutImageId, 512) : null;
    const cutoutFile = cutoutUrl
        ? [{
              key: 'cutout',
              name: 'cutout (current image)',
              format: 'image',
              kind: 'extImage',
              url: cutoutUrl,
              isModelSource: isSource(cutoutUrl),
          }]
        : [];
    const frameFiles = frameIds.map((id, i) => ({
        key: `bed-frame:${id}`,
        name: frameIds.length > 1 ? `bare frame ${i + 1} (bedding removed)` : 'bare frame (bedding removed)',
        format: 'image',
        kind: 'extImage',
        url: catalogImageUrl(id, 512),
        isModelSource: true,
    }));
    const assetFiles = (assets || []).map((a) => ({
        key: `asset:${a.id}`,
        name: a.label ? `${a.label}.${a.assetFormat}` : a.assetFormat,
        format: a.assetFormat,
        kind: isModel(a.assetFormat) ? 'model' : a.assetFormat === 'image' ? 'image' : 'file',
        assetId: a.id,
        byteSize: a.byteSize,
        createdAt: a.createdAt,
        asset: a,
    }));
    const extFiles = (externalImages || []).map((url, i) => ({
        key: `ext:${i}`,
        name: `product image ${i + 1}`,
        format: 'image',
        kind: 'extImage',
        url,
        isModelSource: isSource(url),
    }));
    return [...cutoutFile, ...frameFiles, ...assetFiles, ...extFiles];
};

const thumbBox = {
    width: 36,
    height: 36,
    flexShrink: 0,
    borderRadius: 4,
    objectFit: 'cover',
    background: '#f0f0f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#888',
};

/** Row thumbnail: image preview for images, a model icon for .glb/.babylon, else a file icon. */
const FileThumb = ({ file, itemId }) => {
    if (file.url) return <img src={file.url} alt="" style={thumbBox} />;
    if (file.kind === 'image' && file.assetId) return <AssetImage itemId={itemId} assetId={file.assetId} thumb />;
    if (file.kind === 'model') return <span style={thumbBox}><CodepenOutlined /></span>;
    return <span style={thumbBox}><FileOutlined /></span>;
};

/** Most recent .glb, else first model, else first image, else first file. */
const defaultKey = (files) => {
    const models = files.filter((f) => f.kind === 'model');
    const glbs = models
        .filter((f) => f.format === 'glb')
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    const pick =
        glbs[0] || models[0] || files.find((f) => f.kind === 'image' || f.kind === 'extImage') || files[0];
    return pick?.key ?? null;
};

/** Loads an authenticated asset image as a blob and renders it — full-size, or a 36px `thumb`. */
const AssetImage = ({ itemId, assetId, thumb }) => {
    const [src, setSrc] = useState(null);
    useEffect(() => {
        let url = null;
        let cancelled = false;
        ingestionApi
            .getAssetBlob(itemId, assetId)
            .then((blob) => {
                if (cancelled) return;
                url = URL.createObjectURL(blob);
                setSrc(url);
            })
            .catch((e) => {
                if (!thumb) message.error(e.message || 'Failed to load image');
            });
        return () => {
            cancelled = true;
            if (url) URL.revokeObjectURL(url);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemId, assetId]);
    if (thumb) {
        return src ? <img src={src} alt="" style={thumbBox} /> : <span style={thumbBox}><Spin size="small" /></span>;
    }
    if (!src) return <Spin />;
    return <img src={src} alt="asset" style={{ maxWidth: '100%', maxHeight: 320, objectFit: 'contain' }} />;
};

/** Downloads a (possibly cross-origin) image URL; falls back to opening it if CORS blocks the fetch. */
const downloadUrl = async (url, name) => {
    try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const obj = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = obj;
        a.download = name;
        a.click();
        URL.revokeObjectURL(obj);
    } catch {
        window.open(url, '_blank', 'noopener');
    }
};

export const VariantAssetViewer = ({
    itemId,
    variantId,
    assets,
    cutoutImageId,
    modelImageUrls,
    bedFrameImageIds,
    uploadAccept,
    uploadLabel,
    onUpload,
    onDownload,
    onUseAsAsset,
}) => {
    const uploadInputRef = useRef(null);
    const [useAsBusy, setUseAsBusy] = useState(null);
    const [hasContainer, setHasContainer] = useState(false);
    // Pull the current scraped images live (same source as the step-3 gallery) so
    // the list reflects the latest scrape without waiting on a re-extraction.
    const [externalImages, setExternalImages] = useState([]);
    useEffect(() => {
        let cancelled = false;
        ingestionApi
            .getVariantImages(itemId, variantId)
            .then((r) => !cancelled && setExternalImages(r.candidates || []))
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [itemId, variantId]);

    const files = useMemo(
        () => buildFiles(assets, externalImages, cutoutImageId, modelImageUrls, bedFrameImageIds),
        [assets, externalImages, cutoutImageId, modelImageUrls, bedFrameImageIds],
    );
    // Only ONE image is the active diffuse texture — the newest diffuse-labeled
    // original. Older rows (pre-cleanup leftovers) get the action button back.
    const activeDiffuseId = useMemo(() => {
        const diffuse = (assets || [])
            .filter((a) => a.kind === 'original' && a.assetFormat === 'image' && DIFFUSE_LABELS.includes(a.label))
            .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        return diffuse[0]?.id ?? null;
    }, [assets]);
    const signature = files.map((f) => f.key).join(',');
    const [selectedKey, setSelectedKey] = useState(null);

    useEffect(() => {
        setSelectedKey((cur) => (files.some((f) => f.key === cur) ? cur : defaultKey(files)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signature]);

    const selected = files.find((f) => f.key === selectedKey) || files[0];

    // Container detection is per-loaded-model; reset when the selection changes so a
    // stale "has container" tag never lingers onto a different file.
    useEffect(() => {
        setHasContainer(false);
    }, [selectedKey]);

    const uploadRow = onUpload ? (
        <>
            <input
                ref={uploadInputRef}
                type="file"
                accept={uploadAccept}
                style={{ display: 'none' }}
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) onUpload(file);
                    e.target.value = '';
                }}
            />
            <Button
                type="primary"
                ghost
                block
                icon={<UploadOutlined />}
                style={{ marginTop: 2, height: 46 }}
                onClick={() => uploadInputRef.current?.click()}
            >
                {uploadLabel || 'Upload file'}
            </Button>
        </>
    ) : null;

    if (files.length === 0) {
        return (
            <div>
                <Empty description="No files yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                {uploadRow && <div style={{ marginTop: 8, maxWidth: 260 }}>{uploadRow}</div>}
            </div>
        );
    }

    const renderViewer = () => {
        if (!selected) return null;
        if (selected.kind === 'model') {
            return (
                <ModelViewer
                    key={selected.key}
                    itemId={itemId}
                    assetId={selected.assetId}
                    format={selected.format}
                    onLoaded={(result) =>
                        setHasContainer((result.meshes || []).some((m) => isContainerMeshName(m.name)))
                    }
                />
            );
        }
        if (selected.kind === 'image') {
            return <AssetImage key={selected.key} itemId={itemId} assetId={selected.assetId} />;
        }
        if (selected.kind === 'extImage') {
            return (
                <img
                    key={selected.key}
                    src={selected.url}
                    alt={selected.name}
                    style={{ maxWidth: '100%', maxHeight: 320, objectFit: 'contain' }}
                />
            );
        }
        return <Text type="secondary">This file type can't be previewed — download it instead.</Text>;
    };

    return (
        <div style={{ display: 'flex', gap: 16, alignItems: 'stretch' }}>
            <div style={{ width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {/* Scrolls past ~10 rows; the upload button below stays pinned. */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 520, overflowY: 'auto', paddingRight: 4 }}>
                {files.map((f) => {
                    const active = f.key === selectedKey;
                    const viewable = f.kind !== 'file';
                    return (
                        <div
                            key={f.key}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                border: active ? '1px solid #1677ff' : '1px solid #f0f0f0',
                                borderRadius: 6,
                                padding: 4,
                            }}
                        >
                            <FileThumb file={f} itemId={itemId} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <Text ellipsis style={{ display: 'block', fontSize: 12 }} title={f.name}>
                                    {f.name}
                                </Text>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    {f.byteSize != null && (
                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                            {formatBytes(f.byteSize)}
                                        </Text>
                                    )}
                                    {f.isModelSource && (
                                        <Tag color="blue" style={{ marginInlineEnd: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>
                                            used for model
                                        </Tag>
                                    )}
                                </div>
                            </div>
                            <Tooltip title={active ? 'Viewing' : 'View'}>
                                <Button
                                    size="small"
                                    type={active ? 'primary' : 'text'}
                                    icon={<EyeOutlined />}
                                    disabled={!viewable}
                                    onClick={() => setSelectedKey(f.key)}
                                />
                            </Tooltip>
                            <Tooltip title="Download">
                                <Button
                                    size="small"
                                    type="text"
                                    icon={<DownloadOutlined />}
                                    onClick={() =>
                                        f.asset
                                            ? onDownload?.(f.asset)
                                            : downloadUrl(f.url, `${f.name.replace(/\s+/g, '-')}.jpg`)
                                    }
                                />
                            </Tooltip>
                            {onUseAsAsset && (f.kind === 'image' || f.kind === 'extImage') && (
                                f.asset && f.asset.id === activeDiffuseId ? (
                                    <Tag color="success" style={{ marginInlineEnd: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>
                                        asset
                                    </Tag>
                                ) : (
                                    <Tooltip title="Use as asset — this image becomes the final texture">
                                        <Button
                                            size="small"
                                            type="text"
                                            icon={<CheckCircleOutlined />}
                                            loading={useAsBusy === f.key}
                                            disabled={!!useAsBusy}
                                            onClick={async () => {
                                                setUseAsBusy(f.key);
                                                try {
                                                    await onUseAsAsset(f.asset ? { assetId: f.asset.id } : { url: f.url });
                                                } finally {
                                                    setUseAsBusy(null);
                                                }
                                            }}
                                        />
                                    </Tooltip>
                                )
                            )}
                        </div>
                    );
                })}
                </div>
                {uploadRow}
            </div>

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {selected?.kind === 'model' && hasContainer && (
                    <div style={{ textAlign: 'right' }}>
                        <Tag color="success">
                            Has container plane
                        </Tag>
                    </div>
                )}
                <div
                    style={{
                        flex: 1,
                        minHeight: 320,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#fafafa',
                        borderRadius: 6,
                        padding: 8,
                    }}
                >
                    {renderViewer()}
                </div>
            </div>
        </div>
    );
};
